import CronGenerator from 'components/CronGenerator/CronGenerator';
import Text from 'components/Text/Text';
import ActivationScheduleInput from './components/ActivationScheduleInput';
import DeactivationScheduleInput from './components/DeactivationScheduleInput';
import React, { useContext, useEffect } from 'react';
import { Form } from 'antd';
import BtnBlock from './components/BtnBlock';
import NameField from './components/NameField';
import CustomerSelect from './components/CustomerSelect';
import DescriptionField from './components/DescriptionField';
import RestaurantPricesField from './components/RestaurantPricesField/RestaurantPricesField';
import ModifierGroupField from './components/ModifierGroupField/ModifierGroupField';
import IsDisabledField from './components/IsDisabledField/IsDisabledField';
import IsRestrictedField from './components/IsRestrictedField/IsRestrictedField';
import IsPreselectedField from './components/IsPreselectedField/IsPreselectedField';
import QuantityRegulationField from './components/QuantityRegulationField/QuantityRegulationField';
import { IRestaurantModifierForCreateModifier } from 'types';
import {
	CreateModifierContext,
	ICreateModifierContext,
} from '../../../../../createModifierState/createModifierContext';
import { preventDefaultForEnterKey } from 'utils/helpers';
import style from './InputFields.module.scss';

const InputFields: React.FC = () => {
	const {
		changeTypeOfModalWindow,
		setDataForSubmit,
		setCustomer,
		setFetchedCustomers,
		changeName,
		changeDescription,
		setRestaurantsForPrice,
		setFetchedRestaurants,
		changePrice,
		addPrices,
		removePrice,
		setModifierGroups,
		setFetchedModifierGroups,
		setDisabledInRestaurants,
		setRestrictedInRestaurants,
		setPreselectedInRestaurants,
		setQuantityRegulationRestaurants,
		changeQuantityMaxLimit,
		changeActivationSchedule,
		changeDeactivationSchedule,
		fileId,
		selectedCustomer,
		selectedModifierGroups,
		name,
		description,
		restaurantsForPrice,
		price,
		totalPrices,
		isDisabledInRestaurants,
		isRestrictedInRestaurants,
		isPreselectedInRestaurants,
		quantityRegulationRestaurants,
		quantityMaxLimit,
		translations,
		fetchedRestaurants,
		fetchedModifierGroups,
		activationSchedule,
		deactivationSchedule,
		defaultCustomerLanguage,
	} = useContext<ICreateModifierContext>(CreateModifierContext);
	const [form] = Form.useForm();

	useEffect(() => {
		form.setFieldsValue({ selectedCustomer });
		form.setFieldsValue({ restaurantsForPrice });
		form.setFieldsValue({ price });
		form.setFieldsValue({ selectedModifierGroups });
		form.setFieldsValue({ name });
		form.setFieldsValue({ description });
		form.setFieldsValue({ isDisabledInRestaurants });
		form.setFieldsValue({ isRestrictedInRestaurants });
		form.setFieldsValue({ isPreselectedInRestaurants });
		form.setFieldsValue({ quantityRegulationRestaurants });
		form.setFieldsValue({ quantityMaxLimit });
		form.setFieldsValue({ activationSchedule });
		form.setFieldsValue({ deactivationSchedule });
	}, [
		selectedCustomer,
		restaurantsForPrice,
		price,
		selectedModifierGroups,
		name,
		description,
		isDisabledInRestaurants,
		isRestrictedInRestaurants,
		isPreselectedInRestaurants,
		quantityRegulationRestaurants,
		quantityMaxLimit,
		activationSchedule,
		deactivationSchedule,
	]);

	const submit = async () => {
		const defaultTranslationIsEmpty =
			translations.find((translation) => translation.languageId === defaultCustomerLanguage?.id)?.name === '';
		if (defaultTranslationIsEmpty) {
			changeTypeOfModalWindow('defaultTranslationIsEmpty');
			return;
		}

		const finalRestaurantModifiers: IRestaurantModifierForCreateModifier[] = totalPrices.map((el) => {
			const isDisabled = isDisabledInRestaurants.find(
				(disabledRestaurant) => disabledRestaurant === el.restaurantId,
			);
			const isRestricted = isRestrictedInRestaurants.find(
				(restrictedRestaurant) => restrictedRestaurant === el.restaurantId,
			);
			const isPreselected = isPreselectedInRestaurants.find(
				(preselectedRestaurant) => preselectedRestaurant === el.restaurantId,
			);
			const hasQuantityMaxLimit = quantityRegulationRestaurants.find(
				(quantityRegulationRestaurant) => quantityRegulationRestaurant === el.restaurantId,
			);
			return {
				restaurantId: el.restaurantId,
				isDisabled: !!isDisabled,
				isRestricted: !!isRestricted,
				isPreselected: !!isPreselected,
				price: Number(el.price),
				quantityMaxLimit: hasQuantityMaxLimit ? quantityMaxLimit : null,
				forceDisable: false,
			};
		});

		setDataForSubmit({
			fileId,
			name,
			description,
			customerId: selectedCustomer || '',
			restaurantsModifiers: finalRestaurantModifiers,
			modifierGroupIds: selectedModifierGroups,
			activationCron: activationSchedule || null,
			deactivationCron: deactivationSchedule || null,
			translations,
		});
		changeTypeOfModalWindow('toConfirmSubmit');
	};

	const revalidateForm = form.validateFields;

	return (
		<div className={style.container} data-test="InputFields">
			<Form form={form} name="basic" onFinish={submit} onKeyPress={preventDefaultForEnterKey} layout="vertical">
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="mainInfoHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<CustomerSelect setCustomer={setCustomer} setFetchedCustomers={setFetchedCustomers} />
					<NameField changeName={changeName} />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="descriptionHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<DescriptionField changeDescription={changeDescription} />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="restaurantsModifierGroupsHeadingHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<RestaurantPricesField
						price={price}
						restaurantsForPrice={restaurantsForPrice}
						fetchedRestaurants={fetchedRestaurants}
						totalPrices={totalPrices}
						selectedCustomer={selectedCustomer}
						revalidateForm={revalidateForm}
						setFetchedRestaurants={setFetchedRestaurants}
						setRestaurantsForPrice={setRestaurantsForPrice}
						changePrice={changePrice}
						addPrices={addPrices}
						removePrice={removePrice}
					/>
					<ModifierGroupField
						fetchedModifierGroups={fetchedModifierGroups}
						totalPrices={totalPrices}
						selectedModifierGroups={selectedModifierGroups}
						setFetchedModifierGroups={setFetchedModifierGroups}
						setModifierGroups={setModifierGroups}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="inStopHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<IsDisabledField
						isDisabledInRestaurants={isDisabledInRestaurants}
						fetchedRestaurants={fetchedRestaurants}
						totalPrices={totalPrices}
						setDisabledInRestaurants={setDisabledInRestaurants}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="isRestrictedHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<IsRestrictedField
						totalPrices={totalPrices}
						isRestrictedInRestaurants={isRestrictedInRestaurants}
						fetchedRestaurants={fetchedRestaurants}
						setRestrictedInRestaurants={setRestrictedInRestaurants}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="preselectedModifiersHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<IsPreselectedField
						totalPrices={totalPrices}
						isPreselectedInRestaurants={isPreselectedInRestaurants}
						fetchedRestaurants={fetchedRestaurants}
						setPreselectedInRestaurants={setPreselectedInRestaurants}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="quantityRegulationHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<QuantityRegulationField
						setQuantityRegulationRestaurants={setQuantityRegulationRestaurants}
						changeQuantityMaxLimit={changeQuantityMaxLimit}
						quantityRegulationRestaurants={quantityRegulationRestaurants}
						fetchedRestaurants={fetchedRestaurants}
						totalPrices={totalPrices}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="scheduleHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<div className={style.row}>
						<ActivationScheduleInput changeActivationSchedule={changeActivationSchedule} />
						<DeactivationScheduleInput changeDeactivationSchedule={changeDeactivationSchedule} />
					</div>
					<CronGenerator />
				</div>
				<BtnBlock />
			</Form>
		</div>
	);
};

export default InputFields;
